// ---------------------------------------------------------------------------
// Business Screening Results — Display and resolve business screening hits
// ---------------------------------------------------------------------------

import { StatusBadge } from './StatusBadge';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScreeningHit {
  hitId: string;
  entityName: string;
  list: string;
  listCategory: string;
  matchScore: number;
  matchedFields: string[];
  context: string | null;
}

export interface BusinessScreeningData {
  resultId: string;
  hits: ScreeningHit[];
  clearanceCount: number;
  riskLevel: string;
  completedAt: string;
}

interface BusinessScreeningResultsProps {
  /** Screening result data to display. */
  data: BusinessScreeningData | null;
  /** Callback when a hit is resolved (confirmed/false positive). */
  onResolveHit?: (hitId: string, resolution: 'confirmed_match' | 'false_positive') => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RISK_LEVEL_COLORS: Record<string, string> = {
  none: 'bg-primary-tint text-primary',
  low: 'bg-primary-tint text-primary',
  medium: 'bg-warning-tint text-warning',
  high: 'bg-warning-tint text-warning',
  critical: 'bg-danger-tint text-danger',
};

const LIST_CATEGORY_LABELS: Record<string, string> = {
  sanctions: 'Sanctions',
  pep: 'PEP',
  adverse_media: 'Adverse Media',
  enforcement: 'Enforcement',
  internal_watchlist: 'Internal Watchlist',
};

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 90
      ? 'bg-danger-tint text-danger border-danger/30'
      : score >= 75
        ? 'bg-warning-tint text-warning border-warning/30'
        : score >= 50
          ? 'bg-warning-tint text-warning border-warning/30'
          : 'bg-surface-sunken text-ink border-hairline';

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${color}`}
    >
      {score}%
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BusinessScreeningResults({
  data,
  onResolveHit,
}: BusinessScreeningResultsProps) {
  const f = useFormatters();
  if (!data) {
    return (
      <div className="rounded-sm border border-hairline bg-surface-sunken p-6 text-center text-sm text-ink-muted">
        No screening results available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-surface-raised rounded-sm shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-ink">
              Business Screening Results
            </h3>
            <p className="mt-1 text-xs text-ink-muted">
              Completed: {f.date(data.completedAt, { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-end">
              <p className="text-xs text-ink-muted">Risk Level</p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium ${
                  RISK_LEVEL_COLORS[data.riskLevel] ?? 'bg-surface-sunken text-ink'
                }`}
              >
                {data.riskLevel.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="text-end">
              <p className="text-xs text-ink-muted">Hits</p>
              <span className="text-sm font-semibold text-ink">
                {data.hits.length}
              </span>
            </div>
            <div className="text-end">
              <p className="text-xs text-ink-muted">Cleared</p>
              <span className="text-sm font-semibold text-primary">
                {data.clearanceCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Hit list */}
      {data.hits.length === 0 ? (
        <div className="rounded-sm border border-primary/30 bg-primary-tint p-4 text-center text-sm text-primary">
          No screening hits found. All checks cleared.
        </div>
      ) : (
        <div className="bg-surface-raised rounded-sm shadow overflow-hidden">
          <div className="px-4 py-3 bg-surface-sunken border-b border-hairline">
            <h3 className="text-sm font-semibold text-ink">
              Watchlist Hits ({data.hits.length})
            </h3>
          </div>
          <ul className="divide-y divide-hairline" role="list" aria-label="Screening hits">
            {data.hits.map((hit) => (
              <li key={hit.hitId} className="px-4 py-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-ink">
                        {hit.entityName}
                      </p>
                      <ScoreBadge score={hit.matchScore} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <StatusBadge
                        value={
                          LIST_CATEGORY_LABELS[hit.listCategory] ??
                          hit.listCategory
                        }
                      />
                      <span className="text-xs text-ink-muted">
                        List: {hit.list}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {hit.matchedFields.map((field) => (
                        <span
                          key={field}
                          className="inline-flex items-center px-1.5 py-0.5 rounded bg-surface-sunken text-xs text-ink-muted"
                        >
                          {field.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                    {hit.context && (
                      <p className="mt-1 text-xs text-ink-muted italic">
                        {hit.context}
                      </p>
                    )}
                  </div>
                  {onResolveHit && (
                    <div className="flex gap-1 ms-4">
                      <button
                        type="button"
                        onClick={() => onResolveHit(hit.hitId, 'confirmed_match')}
                        className="px-2 py-1 text-xs font-medium bg-danger-tint text-danger rounded hover:bg-danger-tint transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => onResolveHit(hit.hitId, 'false_positive')}
                        className="px-2 py-1 text-xs font-medium bg-primary-tint text-primary rounded hover:bg-primary-tint transition-colors"
                      >
                        False Positive
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
